import { useMemo, useEffect } from "react";
import { useHistory } from 'react-router';
import { uniq } from "underscore";

import {
	LanguagesAbbr,
	Session,
	SessionPanelLayoutsTypes
} from "../../../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../../../general-ui/icon";
import { useAppDispatch, useTypedSelector } from "../../../../../../../store/reducers/use-typed-selector";
import { availableLanguages, LanguageOption } from '../../../editor/agenda-editor/select-languages';
import { OptionalComponent } from "utils/optional-component";
import { useGetAdminUrl } from "utils/admin-routing-utils";
import { SessionPanelMap } from "@admin/create/session/navigation/panel/session-panel-route-map";
import languageLabelMap from '../../../../../../../utils/translate-langs-map.json';
import { setEventLanguages } from "store/actions/admin/create-event";
import { showAlert } from "@general-ui/alert/alert-service";

import './panel-block-language-and-captions.scss';

function PanelBlockLanguageAndCaptions(): JSX.Element {
	const history = useHistory();
	const adminPath = useGetAdminUrl();
	const dispatch = useAppDispatch();

	const i18nSettings = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.i18n);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const baseLanguage = useMemo(() => workingEvent?.settings.i18n?.base, [workingEvent]);

	const eventLanguages: string[] = useMemo(() =>
		uniq(workingEvent?.sessions?.reduce((acc: string[], cur) => [...acc, ...cur.languages], []) || []),
		[workingEvent]
	);

	useEffect(() => {
		// Backwards compatibility for events created before event based languages.
		// Set them up if it doesn't exist yet.
		if (!i18nSettings?.event_languages && eventLanguages.length) {
			dispatch(setEventLanguages(eventLanguages as LanguagesAbbr[]));
		}
	}, [dispatch, i18nSettings, eventLanguages]);

	const sessionLanguagesMap: { [key: string]: boolean } = useMemo(() => {
		return availableLanguages([workingSession as Session], undefined)
			.reduce((acc, cur) => ({ ...acc, [cur.code]: true }) , {});
	}, [workingSession]);

	const languageOptions: LanguageOption[] = useMemo(() => {
		if (!i18nSettings?.event_languages) return [];

		return i18nSettings.event_languages.map(eventLang => ({ code: eventLang, label: languageLabelMap[eventLang] }))
			.sort((a, b) => a.label > b.label ? 1 : -1) // Alphabetized
			.sort((a, _) => !sessionLanguagesMap[a.code] ? 1 : -1) // Lang not in session = at the bottom
			.sort((a, _) => a.code === baseLanguage ? -1 : 1); // Base language on top
	}, [i18nSettings?.event_languages, baseLanguage, sessionLanguagesMap]);

	return (
		<div className="panel-block settings-block language-and-captions-settings-block">
			<div className="settings-block-header">
				<h5>Language & Captions</h5>
				<div className="panel-block-edit-container">
					<button onClick={() => {
						if (!i18nSettings?.event_languages) {
							// This should never happen, and will only possibly affect events created before
							// event languages is shipped. The setEventLanguages() above is supposed to rectify this
							// situation, but if it fails and no event languages exist they should refresh and try again.
							console.error('Attempted to load event language panel without event languages');

							showAlert({
								message: 'Could not load language editor',
								description: 'Please try refreshing your page. Contact support if the problem persists.',
								type: 'error'
							});

							return;
						}

						history.push(
							adminPath({
								path: SessionPanelMap[SessionPanelLayoutsTypes.LanguageAndCaptions]
							})
						);
					}}>
						<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />
					</button>
				</div>
			</div>

			{languageOptions.map((lang, idx) => (
				<div className="settings-type" style={idx === 0 ? { marginTop: 16 } : {}} key={lang.code}>
					<div className="left">
						<span className="language-label">{lang.label}</span>
						<span className="language-code">{lang.code}</span>
						<OptionalComponent display={!!(baseLanguage && baseLanguage === lang.code)}>
							<span className="language-is-base">Base</span>
						</OptionalComponent>
					</div>
					<div className="right">
						<OptionalComponent display={!sessionLanguagesMap[lang.code]}>
							{/* TODO - IF both captions and language is off for the session. Captions is TODO, so only works with the language for now */}
							<Icon name={ICONS.VIEWERS_EYE_OFF} size={16} color={COLORS.WHITE} />
						</OptionalComponent>
						<OptionalComponent display={Math.random() < 0}>
							{/* TODO - will depend on session captions settings */}
							<Icon name={ICONS.CAPTIONS} size={16} color={COLORS.WHITE} />
						</OptionalComponent>
						<OptionalComponent display={sessionLanguagesMap[lang.code]}>
							<Icon name={ICONS.DOCUMENTS_REVERSE} size={16} color={COLORS.WHITE} />
						</OptionalComponent>
					</div>
				</div>
			))}
		</div>
	);
}

export default PanelBlockLanguageAndCaptions;